import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { HeadTag } from 'shared/components';
import {
  Box,
  Chip,
  Grid,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import PageHeader from 'shared/components/PageHeader';
import { ReactComponent as WarningIcon } from 'assets/images/warning-incident.svg';
import { ReactComponent as CheckIcon } from 'assets/images/check-incident.svg';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { IncidentService } from 'services/IncidentService';
import { Utils } from 'shared/helpers';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import CustomDateRangePicker from './CustomDateRangePicker';

const COLORS = {
  critical: '#D32F2F',
  high: '#F57C00',
  moderate: '#FFC829',
  low: '#052921',
};

const RotatedYAxisTick = ({ x, y, payload }: any): any => (
  <g transform={`translate(${x},${y})`}>
    <text
      x={0}
      y={0}
      textAnchor="middle"
      fill="#888780"
      fontSize={12}
      transform="rotate(-90)"
      dy={4}
    >
      {payload.value}
    </text>
  </g>
);

const heatmapColumn = [
  'System Failure',
  'Data Breach',
  'Perf. Issue',
  'Hallucination',
  'Bias/Fairness',
  'Content Mod.',
  'Privacy',
  'Ethical',
  'Performance',
  'Regulatory',
  'Other',
];

const FILTER_TYPES = [
  { label: '3M', value: 'LAST_3_MONTHS' },
  { label: '6M', value: 'LAST_6_MONTHS' },
  { label: '12M', value: 'LAST_12_MONTHS' },
];

const heatmapRows = ['Critical', 'High', 'Moderate', 'Low'];

const handleDatesPreset = (preset: any): any => {
  const today = dayjs();
  switch (preset) {
    case 'LAST_3_MONTHS':
      return { from: today.subtract(3, 'month'), to: today };
    case 'LAST_6_MONTHS':
      return { from: today.subtract(6, 'month'), to: today };
    case 'LAST_12_MONTHS':
      return { from: today.subtract(12, 'month'), to: today };
    default:
      return {
        from: today.subtract(12, 'month'),
        to: today,
      };
  }
};

const getFilterTypeFromDates = (start: any, end: any): string => {
  if (!start || !end) return 'CUSTOM';

  const today = dayjs().startOf('day');
  const fromDay = dayjs(start).startOf('day');
  const toDay = dayjs(end).startOf('day');

  if (toDay.isSame(today) && fromDay.isSame(today.subtract(3, 'month')))
    return 'LAST_3_MONTHS';
  if (toDay.isSame(today) && fromDay.isSame(today.subtract(6, 'month')))
    return 'LAST_6_MONTHS';
  if (toDay.isSame(today) && fromDay.isSame(today.subtract(12, 'month')))
    return 'LAST_12_MONTHS';

  return 'CUSTOM';
};

const IncidentsDashboard = (): ReactElement => {
  const navigate = useNavigate();
  const [incidentsData, setIncidentsData] = useState<any>(null);
  const defaultPreset: any = 'LAST_12_MONTHS';
  const defaultDates = handleDatesPreset(defaultPreset);
  const [activeFilterType, setActiveFilterType] = useState<any>(defaultPreset);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<any>([
    defaultDates.from,
    defaultDates.to,
  ]);

  const incidentVolume = useMemo(() => {
    if (!incidentsData?.monthWiseSeverity) return [];
    return Object.entries(incidentsData.monthWiseSeverity).map(
      ([month, severities]: any) => ({
        date: month,
        critical: severities?.Critical || 0,
        high: severities?.High || 0,
        medium: severities?.Medium || 0,
        low: severities?.Low || 0,
      })
    );
  }, [incidentsData]);

  const topCategories = useMemo(() => {
    if (!incidentsData?.incidentTypeWise) return [];
    return Object.entries(incidentsData.incidentTypeWise).map(
      ([category, value]) => ({
        category: category.toUpperCase(),
        value,
      })
    );
  }, [incidentsData]);

  const assignedUsers = useMemo(() => {
    if (!incidentsData?.assignedToWise) return [];
    const colors = ['#FFC829', '#052921', '#3B799F', '#8355C3', '#AC3A69'];
    return Object.entries(incidentsData.assignedToWise).map(
      ([name, value], index) => ({
        name,
        value,
        fill: colors[index % colors.length],
      })
    );
  }, [incidentsData]);

  const heatmapValue = useMemo(() => {
    const initialData: any = { Critical: {}, High: {}, Medium: {}, Low: {} };
    if (!incidentsData?.incidentTypeSeverityWise) return initialData;
    Object.entries(incidentsData.incidentTypeSeverityWise).forEach(
      ([type, severities]: any) => {
        Object.entries(severities).forEach(([severity, count]) => {
          if (initialData[severity]) initialData[severity][type] = count;
        });
      }
    );
    return initialData;
  }, [incidentsData]);

  const maxVol = useMemo(() => {
    if (!incidentVolume.length) return 4;
    return Math.max(
      ...incidentVolume.map(
        (data: any) => data.critical + data.high + data.medium + data.low
      )
    );
  }, [incidentVolume]);

  const maxCat = Math.max(...topCategories.map((data: any) => data.value), 4);
  const maxAssigned = Math.max(
    ...assignedUsers.map((data: any) => data.value),
    4
  );

  const handleFilterTypeClick = (preset: any): any => {
    if (preset === 'CUSTOM') return;
    const { from, to } = handleDatesPreset(preset as any);
    setActiveFilterType(preset);
    setDateRange([from, to]);
  };

  const handleDateRangeChange = (newValue: any): void => {
    setDateRange(newValue);
    const [start, end] = newValue;
    if (start && end) {
      setActiveFilterType(getFilterTypeFromDates(start, end));
    } else {
      setActiveFilterType('CUSTOM');
    }
  };

  useEffect(() => {
    const [startDate, endDate] = dateRange;

    const from = dayjs(startDate);
    const to = dayjs(endDate);

    const fromDate =
      activeFilterType === 'CUSTOM' && from.isValid()
        ? from.format('YYYY-MM-DD')
        : undefined;

    const toDate =
      activeFilterType === 'CUSTOM' && to.isValid()
        ? to.format('YYYY-MM-DD')
        : undefined;
    setIsLoading(true);
    IncidentService.getAllDashboard(activeFilterType, fromDate, toDate)
      .then((response) => {
        const { data } = response.data;
        setIncidentsData(data);
      })
      .catch((err) => {
        Utils.handleErrorResponse(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [activeFilterType, dateRange]);

  return (
    <>
      <PageHeader
        title="Incident Dashboard"
        description="Portfolio health and situational awareness"
        actions={
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, sm: 2 },
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              alignItems: 'center',
              width: { xs: '100%', md: 'auto' },
              minWidth: 0,

              '& .MuiButton-root': {
                minWidth: { xs: 64, sm: 80 },
                height: 48,
                px: 2,
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '13px',
                flexShrink: 0,
              },
            }}
          >
            {FILTER_TYPES.map(({ label, value }) => {
              const isActive = value === activeFilterType;

              return (
                <Button
                  key={value}
                  variant="outlined"
                  size="small"
                  onClick={() => handleFilterTypeClick(value)}
                  sx={{
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? '#fff' : '#7b696e',
                    backgroundColor: isActive ? '#06362b' : 'transparent',
                    borderColor: isActive
                      ? 'rgba(6, 54, 43, 0.5)'
                      : 'rgba(0, 0, 0, 0.12)',

                    '&:hover': {
                      backgroundColor: isActive
                        ? '#06362b'
                        : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  {label}
                </Button>
              );
            })}

            <Box
              sx={{
                flexShrink: 0,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <CustomDateRangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
              />
            </Box>
          </Box>
        }
      />

      <Box className="incident-management incidents">
        <HeadTag title="Incidents Dashboard" />

        {isLoading ? (
          <Box className="empty-content">
            <CircularProgress />
          </Box>
        ) : (
          <Box className="incidents-wrapper">
            {/* Incident Overview */}
            <Box mt={8}>
              <Typography
                sx={{
                  color: '#7E7E7E',
                  fontSize: '15px',
                  fontWeight: 500,
                }}
              >
                Open incidents overview
              </Typography>

              {/* Box */}
              <Box>
                <Grid container mt={6} className="incident-overview-container">
                  <Grid xs={12} md={6}>
                    <Box>
                      <Grid container className="incident-overview-container">
                        <Grid xs={12} md={3} item className="box">
                          <Typography className="text">
                            {incidentsData?.statusWise?.Detected || 0}
                          </Typography>
                          <Chip
                            label="Detected"
                            sx={{
                              background: '#FFDA6E',
                              color: '#A55A22',
                              marginTop: '10px',
                              fontWeight: 600,
                            }}
                            size="small"
                          />
                        </Grid>
                        <Grid xs={12} md={3} item className="box">
                          <Typography className="text">
                            {incidentsData?.statusWise?.Triaged || 0}
                          </Typography>
                          <Chip
                            label="Triaged"
                            sx={{
                              background: '#E0E7FF',
                              color: '#5E5BB8',
                              marginTop: '10px',
                              fontWeight: 600,
                            }}
                            size="small"
                          />
                        </Grid>
                        <Grid xs={12} md={3} item className="box">
                          <Typography className="text">
                            {incidentsData?.statusWise?.Assessed || 0}
                          </Typography>
                          <Chip
                            label="Assessed"
                            sx={{
                              background: '#F3E8FF',
                              color: '#8355C3',
                              marginTop: '10px',
                              fontWeight: 600,
                            }}
                            size="small"
                          />
                        </Grid>
                        <Grid xs={12} md={3} item className="box">
                          <Typography className="text">
                            {incidentsData?.statusWise?.Notified || 0}
                          </Typography>
                          <Chip
                            label="Notified"
                            sx={{
                              background: '#CFFAFE',
                              color: '#3B799F',
                              marginTop: '10px',
                              fontWeight: 600,
                            }}
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                  <Grid xs={12} md={6}>
                    <Box>
                      <Grid container className="incident-overview-container">
                        <Grid xs={12} md={3} item className="box">
                          <Typography className="text">
                            {incidentsData?.statusWise?.Contained || 0}
                          </Typography>
                          <Chip
                            label="Contained"
                            sx={{
                              background: '#FFEDD5',
                              color: '#BC7C5E',
                              marginTop: '10px',
                              fontWeight: 600,
                            }}
                            size="small"
                          />
                        </Grid>
                        <Grid xs={12} md={3} item className="box">
                          <Typography className="text">
                            {incidentsData?.statusWise?.Analyzed || 0}
                          </Typography>
                          <Chip
                            label="Analyzed"
                            sx={{
                              background: '#FCE7F3',
                              color: '#AC3A69',
                              marginTop: '10px',
                              fontWeight: 600,
                            }}
                            size="small"
                          />
                        </Grid>
                        <Grid xs={12} md={3} item className="box">
                          <Typography className="text">
                            {incidentsData?.statusWise?.Resolved || 0}
                          </Typography>
                          <Chip
                            label="Resolved"
                            sx={{
                              background: '#ECFCCB',
                              color: '#587E3A',
                              marginTop: '10px',
                              fontWeight: 600,
                            }}
                            size="small"
                          />
                        </Grid>
                        <Grid xs={12} md={3} item className="box">
                          <Typography className="text">
                            {incidentsData?.statusWise?.Closed || 0}
                          </Typography>
                          <Chip
                            label="Closed"
                            sx={{
                              background: '#E7E7E7',
                              color: '#7E7E7E',
                              marginTop: '10px',
                              fontWeight: 600,
                            }}
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              {/* End of Box */}
            </Box>

            {/* Chips */}
            <Box sx={{ display: 'flex', gap: '1rem' }}>
              <Chip
                label={`${
                  incidentsData?.severityWise?.Critical || 0
                }\u00A0\u00A0\u00A0Critical`}
                sx={{
                  background: '#FA3F3F',
                  color: 'white',
                  marginTop: '10px',
                  fontWeight: 600,
                  minWidth: 112,
                }}
              />
              <Chip
                label={`${
                  incidentsData?.severityWise?.High || 0
                }\u00A0\u00A0\u00A0High`}
                sx={{
                  background: '#E97218',
                  color: 'white',
                  marginTop: '10px',
                  fontWeight: 600,
                  minWidth: 112,
                }}
              />
              <Chip
                label={`${
                  incidentsData?.severityWise?.Medium || 0
                }\u00A0\u00A0\u00A0Moderate`}
                sx={{
                  background: '#FFC829',
                  color: 'white',
                  marginTop: '10px',
                  fontWeight: 600,
                  minWidth: 112,
                }}
              />
              <Chip
                label={`${
                  incidentsData?.severityWise?.Low || 0
                }\u00A0\u00A0\u00A0Low`}
                sx={{
                  background: '#76A81F',
                  color: 'white',
                  marginTop: '10px',
                  fontWeight: 600,
                  minWidth: 112,
                }}
              />
            </Box>

            {/* SLA/SLO */}
            <Box mt={8}>
              <Typography
                sx={{
                  color: '#7E7E7E',
                  fontSize: '15px',
                  fontWeight: 500,
                }}
              >
                SLA / SLO posture
              </Typography>

              {/* Box */}
              <Box mt={6}>
                <Grid container className="incident-sla-container">
                  <Grid xs={12} md={4} item className="box">
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '10px',
                          alignItems: 'center',
                        }}
                      >
                        <WarningIcon />
                        <Typography fontSize={16}>
                          Time to Acknowledge
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                        mt={5}
                      >
                        <Typography fontSize={16} color="#7E7E7E">
                          Median
                        </Typography>
                        <Typography fontSize={16}>7.2d</Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                        mt={5}
                      >
                        <Typography fontSize={16} color="#7E7E7E">
                          p90
                        </Typography>
                        <Typography fontSize={16} color="#DE3A3A">
                          10.1d
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                        mt={5}
                      >
                        <Typography fontSize={16} color="#7E7E7E">
                          Target: 4h / Warn: 8h
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid xs={12} md={4} item className="box">
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '10px',
                          alignItems: 'center',
                        }}
                      >
                        <CheckIcon />
                        <Typography fontSize={16}>Time to Contain</Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                        mt={5}
                      >
                        <Typography fontSize={16} color="#7E7E7E">
                          Median
                        </Typography>
                        <Typography fontSize={16}>-</Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                        mt={5}
                      >
                        <Typography fontSize={16} color="#7E7E7E">
                          p90
                        </Typography>
                        <Typography fontSize={16} color="#DE3A3A">
                          -
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                        mt={5}
                      >
                        <Typography fontSize={16} color="#7E7E7E">
                          Target: 1.0d / Warn: 2.0d
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid xs={12} md={4} item className="box">
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '10px',
                          alignItems: 'center',
                        }}
                      >
                        <CheckIcon />
                        <Typography fontSize={16}>Time to Resolve</Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                        mt={5}
                      >
                        <Typography fontSize={16} color="#7E7E7E">
                          Median
                        </Typography>
                        <Typography fontSize={16}>-</Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                        mt={5}
                      >
                        <Typography fontSize={16} color="#7E7E7E">
                          p90
                        </Typography>
                        <Typography fontSize={16} color="#DE3A3A">
                          -
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                        mt={5}
                      >
                        <Typography fontSize={16} color="#7E7E7E">
                          Target: 3.0d / Warn: 7.0d
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              {/* End of Box */}

              <Box mt={12}>
                <Grid container className="incident-sla-container">
                  <Grid xs={12} item className="box">
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '10px',
                          alignItems: 'center',
                        }}
                      >
                        <WarningIcon />
                        <Typography fontSize={16}>What's urgent now</Typography>
                      </Box>
                      <Box
                        mt={6}
                        sx={{
                          textAlign: 'center',
                        }}
                      >
                        <CheckCircleOutlineIcon
                          sx={{ color: '#052921' }}
                          fontSize="large"
                        />
                        <Typography
                          mb={12}
                          sx={{
                            color: '#7E7E7E',
                            fontSize: '20px',
                          }}
                        >
                          No urgent incidents
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Trends */}
            <Box mt={8}>
              <Typography
                sx={{
                  color: '#7E7E7E',
                  fontSize: '15px',
                  fontWeight: 500,
                }}
              >
                Trends
              </Typography>

              {/* Box */}
              <Box mt={6}>
                <Grid container className="incident-sla-container">
                  <Grid xs={12} md={6} item className="box">
                    <Typography fontSize={16} mb={6}>
                      Incident volume & severity mix
                    </Typography>

                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart
                        data={incidentVolume}
                        margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="date"
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          domain={[0, maxVol]}
                          ticks={[...new Array(maxVol + 1)].map(
                            (_, index) => index
                          )}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            borderRadius: '8px',
                            border: '1px solid #ccc',
                          }}
                        />
                        <Bar
                          dataKey="critical"
                          stackId="a"
                          fill={COLORS.critical}
                        />
                        <Bar dataKey="high" stackId="a" fill={COLORS.high} />
                        <Bar
                          dataKey="medium"
                          stackId="a"
                          fill={COLORS.moderate}
                        />
                        <Bar dataKey="low" stackId="a" fill={COLORS.low} />
                      </BarChart>
                    </ResponsiveContainer>

                    <Box display="flex" gap={2} mt={1}>
                      {Object.entries(COLORS).map(([key, color]) => (
                        <Box
                          key={key}
                          display="flex"
                          alignItems="center"
                          gap={0.5}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              bgcolor: color,
                            }}
                          />
                          <Typography fontSize={12} textTransform="capitalize">
                            {key}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Grid>

                  <Grid xs={12} md={6} item className="box">
                    <Typography fontSize={16} mb={6}>
                      Top incident categories
                    </Typography>

                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart
                        data={topCategories}
                        layout="vertical"
                        margin={{ top: 4, right: 8, left: 8, bottom: 0 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          horizontal={false}
                        />
                        <XAxis
                          type="number"
                          domain={[0, maxCat]}
                          ticks={[0, 1, 2, 3, 4]}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          type="category"
                          dataKey="category"
                          axisLine={false}
                          tickLine={false}
                          width={20}
                          tick={<RotatedYAxisTick />}
                        />
                        <Tooltip
                          contentStyle={{
                            borderRadius: '8px',
                            border: '1px solid #ccc',
                          }}
                        />
                        <Bar dataKey="value" fill={COLORS.low} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Grid>
                </Grid>
              </Box>

              {/* Box */}
              <Box mt={6}>
                <Grid container className="incident-sla-container">
                  <Grid xs={12} item className="box">
                    <Typography fontSize={16} mb={6}>
                      Assigned users
                    </Typography>

                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart
                        data={assignedUsers}
                        layout="vertical"
                        margin={{ top: 4, right: 8, left: 8, bottom: 0 }}
                        barCategoryGap="20%"
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          horizontal={false}
                        />
                        <XAxis
                          type="number"
                          domain={[0, maxAssigned]}
                          ticks={[0, 1, 2, 3, 4]}
                          axisLine={{ stroke: 'black' }}
                          tickLine={false}
                        />

                        <YAxis
                          type="category"
                          dataKey="name"
                          axisLine={{ stroke: 'black' }}
                          tickLine={false}
                          width={80}
                        />
                        <Tooltip
                          contentStyle={{
                            borderRadius: '8px',
                            border: '1px solid #ccc',
                          }}
                        />
                        <Bar dataKey="value">
                          {assignedUsers.map((entry, index) => (
                            <Cell key={index} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Grid>
                </Grid>
              </Box>
              {/* Box */}
            </Box>

            {/* Heatmap */}
            <Box mt={8}>
              <Typography
                sx={{
                  color: '#7E7E7E',
                  fontSize: '15px',
                  fontWeight: 500,
                }}
              >
                Heatmap
              </Typography>

              {/* Box */}
              <Box mt={6}>
                <Grid container className="incident-sla-container">
                  <Grid xs={12} md={12} item className="box">
                    <Typography fontSize={16} mb={6}>
                      Severity x Incident type
                    </Typography>

                    <Box>
                      <Grid container spacing={1} mb={1}>
                        <Grid item xs={1}>
                          <Typography>Severity</Typography>
                        </Grid>
                        {heatmapColumn.map((col) => (
                          <Grid item xs key={col}>
                            <Typography
                              sx={{
                                color: '#7E7E7E',
                                fontSize: '10px',
                              }}
                            >
                              {col}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>

                      {heatmapRows.map((row) => (
                        <Grid container spacing={1} key={row} mb={1}>
                          <Grid item xs={1}>
                            <Typography
                              sx={{
                                color: '#7E7E7E',
                              }}
                            >
                              {row}
                            </Typography>
                          </Grid>

                          {heatmapColumn.map((col) => {
                            const value = heatmapValue[row]?.[col] ?? null;

                            return (
                              <Grid item xs key={col}>
                                <Box
                                  className={`heatmap-box ${
                                    value ? 'active' : ''
                                  }`}
                                  onClick={() => {
                                    if (!value) return;

                                    navigate('/incidents-log', {
                                      state: {
                                        severity: row,
                                        incident_type: col,
                                      },
                                    });
                                  }}
                                  sx={{
                                    cursor: value ? 'pointer' : 'default',
                                  }}
                                >
                                  {value ?? ''}
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default IncidentsDashboard;
